import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Volume2, VolumeX, X, Maximize2, Minimize2, Play, Pause, Film } from 'lucide-react';
import Hls from 'hls.js';

/**
 * FloatingMiniVideoPlayer
 * Draggable Picture-in-Picture (PiP) floating product video player with:
 * - Touch & pointer drag gestures with inertia-free 60fps transform updates
 * - Safe viewport boundary clamping (respects display cutouts, top nav, and bottom sticky bar)
 * - Drag vs. tap disambiguation (prevents accidental opens/navigation while dragging)
 * - Play/Pause, Mute/Unmute, Minimize-to-pill, Expand-to-fullscreen, and Close controls
 * - Single video/HLS instance with full resource cleanup on close/unmount
 */
const FloatingMiniVideoPlayer = ({
    videoUrl,
    posterUrl,
    productName = '',
    isRTL = false,
    initiallyMuted = true,
    onExpandChange
}) => {
    const [isClosed, setIsClosed] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(initiallyMuted);
    const [hasError, setHasError] = useState(false);
    const [isBuffering, setIsBuffering] = useState(true);

    // Dimensions of mini-player
    const getMiniDimensions = useCallback(() => {
        if (typeof window === 'undefined') return { width: 136, height: 96 };
        const vw = window.innerWidth;
        if (vw < 380) return { width: 120, height: 86 };
        if (vw < 768) return { width: 138, height: 98 };
        return { width: 168, height: 118 };
    }, []);

    const getSafeBounds = useCallback((isMin = false) => {
        if (typeof window === 'undefined') {
            return { minX: 12, maxX: 240, minY: 72, maxY: 500 };
        }
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const dims = isMin ? { width: 108, height: 38 } : getMiniDimensions();
        const topSafe = 68; // below top navigation/cutout
        const bottomStickyHeight = vw <= 1023 ? 88 : 28; // above sticky purchase bar
        const margin = 12;

        return {
            minX: margin,
            maxX: Math.max(margin, vw - dims.width - margin),
            minY: topSafe,
            maxY: Math.max(topSafe + 20, vh - dims.height - bottomStickyHeight)
        };
    }, [getMiniDimensions]);

    // Default position: right side (or left in RTL) near bottom of hero image (matching reference screenshot)
    const getInitialPosition = useCallback(() => {
        if (typeof window === 'undefined') return { x: 200, y: 240 };
        const bounds = getSafeBounds(false);
        const vh = window.innerHeight;
        const defaultY = Math.min(bounds.maxY, Math.max(bounds.minY, Math.round(vh * 0.28)));
        const defaultX = isRTL ? bounds.minX : bounds.maxX;
        return { x: defaultX, y: defaultY };
    }, [getSafeBounds, isRTL]);

    const [position, setPosition] = useState(getInitialPosition);
    const posRef = useRef(position);
    posRef.current = position;

    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const dragStateRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
        moved: false
    });
    const savedScrollYRef = useRef(0);

    // Reset state when videoUrl changes
    useEffect(() => {
        setHasError(false);
        setIsClosed(false);
        setIsMinimized(false);
        setIsExpanded(false);
        setIsBuffering(true);
        setPosition(getInitialPosition());
    }, [videoUrl, getInitialPosition]);

    // Clamp position on viewport resize or orientation change
    useEffect(() => {
        const handleResize = () => {
            const bounds = getSafeBounds(isMinimized);
            setPosition(prev => ({
                x: Math.min(bounds.maxX, Math.max(bounds.minX, prev.x)),
                y: Math.min(bounds.maxY, Math.max(bounds.minY, prev.y))
            }));
        };
        window.addEventListener('resize', handleResize, { passive: true });
        window.addEventListener('orientationchange', handleResize, { passive: true });
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [getSafeBounds, isMinimized]);

    // Initialize / clean up single video & HLS instance
    useEffect(() => {
        if (!videoUrl || isClosed || isMinimized || hasError) {
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
            return;
        }

        const videoEl = videoRef.current;
        if (!videoEl) return;

        let cancelled = false;
        setIsBuffering(true);

        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        videoEl.muted = true;
        videoEl.defaultMuted = true;
        videoEl.playsInline = true;

        const attemptPlay = () => {
            if (!videoEl) return;
            videoEl.play().catch(() => {
                const onFirstInteraction = () => {
                    if (videoRef.current) {
                        videoRef.current.muted = true;
                        videoRef.current.play().catch(() => {});
                    }
                    window.removeEventListener('click', onFirstInteraction);
                    window.removeEventListener('touchstart', onFirstInteraction);
                };
                window.addEventListener('click', onFirstInteraction, { once: true, passive: true });
                window.addEventListener('touchstart', onFirstInteraction, { once: true, passive: true });
            });
        };

        const isHlsStream = videoUrl.includes('.m3u8');
        if (isHlsStream && Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: false,
                maxBufferLength: 10,
                maxMaxBufferLength: 20
            });
            hlsRef.current = hls;
            hls.loadSource(videoUrl);
            hls.attachMedia(videoEl);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (cancelled) return;
                setIsBuffering(false);
                if (isPlaying) {
                    attemptPlay();
                }
            });
            hls.on(Hls.Events.ERROR, (_, data) => {
                if (data?.fatal) {
                    setHasError(true);
                    setIsBuffering(false);
                    hls.destroy();
                    hlsRef.current = null;
                }
            });
        } else {
            videoEl.src = videoUrl;
            const onCanPlay = () => {
                if (cancelled) return;
                setIsBuffering(false);
                if (isPlaying) {
                    attemptPlay();
                }
            };
            const onErr = () => {
                if (cancelled) return;
                setHasError(true);
                setIsBuffering(false);
            };
            videoEl.addEventListener('loadeddata', onCanPlay);
            videoEl.addEventListener('canplay', onCanPlay);
            videoEl.addEventListener('error', onErr);

            return () => {
                cancelled = true;
                videoEl.removeEventListener('loadeddata', onCanPlay);
                videoEl.removeEventListener('canplay', onCanPlay);
                videoEl.removeEventListener('error', onErr);
                try {
                    videoEl.pause();
                    videoEl.removeAttribute('src');
                    videoEl.load();
                } catch (_) {}
            };
        }

        return () => {
            cancelled = true;
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [videoUrl, isClosed, isMinimized, hasError]);

    // Sync play/pause & mute state with video element
    useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl || isClosed || isMinimized || hasError) return;
        videoEl.muted = isMuted;
        if (isPlaying) {
            videoEl.play().catch(() => setIsPlaying(false));
        } else {
            videoEl.pause();
        }
    }, [isPlaying, isMuted, isClosed, isMinimized, hasError]);

    // Handle Expand / Collapse while preserving scroll position
    const openExpanded = useCallback(() => {
        if (typeof window !== 'undefined') {
            savedScrollYRef.current = window.scrollY;
        }
        setIsExpanded(true);
        setIsPlaying(true);
        onExpandChange?.(true);
    }, [onExpandChange]);

    const closeExpanded = useCallback(() => {
        setIsExpanded(false);
        onExpandChange?.(false);
        if (typeof window !== 'undefined') {
            const targetY = savedScrollYRef.current;
            requestAnimationFrame(() => {
                window.scrollTo({ top: targetY, behavior: 'instant' });
            });
        }
    }, [onExpandChange]);

    // Unified Pointer / Touch Drag Handlers
    const startDrag = useCallback((clientX, clientY) => {
        dragStateRef.current = {
            active: true,
            startX: clientX,
            startY: clientY,
            originX: posRef.current.x,
            originY: posRef.current.y,
            moved: false
        };
    }, []);

    const moveDrag = useCallback((clientX, clientY) => {
        const st = dragStateRef.current;
        if (!st.active) return;
        const dx = clientX - st.startX;
        const dy = clientY - st.startY;
        if (!st.moved && Math.hypot(dx, dy) > 6) {
            st.moved = true;
        }
        if (st.moved) {
            const bounds = getSafeBounds(isMinimized);
            const nextX = Math.min(bounds.maxX, Math.max(bounds.minX, st.originX + dx));
            const nextY = Math.min(bounds.maxY, Math.max(bounds.minY, st.originY + dy));
            setPosition({ x: nextX, y: nextY });
        }
    }, [getSafeBounds, isMinimized]);

    const endDrag = useCallback(() => {
        const st = dragStateRef.current;
        if (!st.active) return;
        st.active = false;
        // Snap gently to nearest horizontal screen edge if user drags near edge
        if (st.moved && typeof window !== 'undefined') {
            const bounds = getSafeBounds(isMinimized);
            const mid = (bounds.minX + bounds.maxX) / 2;
            setPosition(prev => ({
                x: prev.x < mid ? bounds.minX : bounds.maxX,
                y: Math.min(bounds.maxY, Math.max(bounds.minY, prev.y))
            }));
        }
    }, [getSafeBounds, isMinimized]);

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!dragStateRef.current.active) return;
            moveDrag(e.clientX, e.clientY);
        };
        const onMouseUp = () => {
            if (!dragStateRef.current.active) return;
            endDrag();
        };
        window.addEventListener('mousemove', onMouseMove, { passive: true });
        window.addEventListener('mouseup', onMouseUp, { passive: true });
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [moveDrag, endDrag]);

    if (!videoUrl || hasError) return null;

    // When closed, show a small unobtrusive reopen pill at the bottom-end of the screen
    if (isClosed) {
        return (
            <button
                type="button"
                onClick={() => {
                    setIsClosed(false);
                    setIsMinimized(false);
                    setIsPlaying(true);
                }}
                aria-label={isRTL ? 'تشغيل فيديو المنتج' : 'Open product video'}
                style={{
                    position: 'fixed',
                    bottom: '92px',
                    right: isRTL ? 'auto' : '14px',
                    left: isRTL ? '14px' : 'auto',
                    zIndex: 9990,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 12px',
                    borderRadius: '999px',
                    background: 'rgba(17, 17, 17, 0.88)',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.25)',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)'
                }}
            >
                <Play size={13} fill="#FE2C55" color="#FE2C55" />
                <span>{isRTL ? 'فيديو المنتج' : 'Video'}</span>
            </button>
        );
    }

    // Minimized draggable pill state
    if (isMinimized && !isExpanded) {
        return (
            <div
                role="button"
                tabIndex={0}
                onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
                onTouchStart={(e) => {
                    const t = e.touches[0];
                    if (t) startDrag(t.clientX, t.clientY);
                }}
                onTouchMove={(e) => {
                    const t = e.touches[0];
                    if (t) moveDrag(t.clientX, t.clientY);
                }}
                onTouchEnd={endDrag}
                onClick={() => {
                    if (!dragStateRef.current.moved) {
                        setIsMinimized(false);
                    }
                }}
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
                    zIndex: 9992,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    borderRadius: '999px',
                    background: 'rgba(17, 17, 17, 0.92)',
                    color: '#ffffff',
                    border: '1.5px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.32)',
                    cursor: 'grab',
                    touchAction: 'none',
                    userSelect: 'none',
                    fontSize: '12px',
                    fontWeight: 700
                }}
            >
                <Film size={14} color="#FE2C55" />
                <span>{isRTL ? 'فيديو' : 'Video'}</span>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsClosed(true);
                    }}
                    aria-label="Close video"
                    style={{
                        background: 'rgba(255,255,255,0.16)',
                        border: 'none',
                        color: '#ffffff',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        padding: 0
                    }}
                >
                    <X size={12} />
                </button>
            </div>
        );
    }

    const dims = getMiniDimensions();

    return (
        <>
            {/* Floating Draggable Mini-Player */}
            <div
                className="lux-floating-mini-player"
                onMouseDown={(e) => {
                    if (!isExpanded) startDrag(e.clientX, e.clientY);
                }}
                onTouchStart={(e) => {
                    if (isExpanded) return;
                    const t = e.touches[0];
                    if (t) startDrag(t.clientX, t.clientY);
                }}
                onTouchMove={(e) => {
                    if (isExpanded) return;
                    const t = e.touches[0];
                    if (t) moveDrag(t.clientX, t.clientY);
                }}
                onTouchEnd={() => {
                    if (!isExpanded) endDrag();
                }}
                style={
                    isExpanded
                        ? {
                              position: 'fixed',
                              inset: 0,
                              zIndex: 100020,
                              background: 'rgba(0, 0, 0, 0.94)',
                              backdropFilter: 'blur(12px)',
                              WebkitBackdropFilter: 'blur(12px)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: 'env(safe-area-inset-top, 16px) 16px env(safe-area-inset-bottom, 24px)'
                          }
                        : {
                              position: 'fixed',
                              left: 0,
                              top: 0,
                              width: `${dims.width}px`,
                              height: `${dims.height}px`,
                              transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
                              zIndex: 9992,
                              borderRadius: '12px',
                              overflow: 'hidden',
                              background: '#ffffff',
                              border: '2px solid rgba(255, 255, 255, 0.95)',
                              boxShadow: '0 10px 28px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(0, 0, 0, 0.08)',
                              cursor: 'grab',
                              touchAction: 'none',
                              userSelect: 'none'
                          }
                }
            >
                {/* Video Surface */}
                <div
                    onClick={() => {
                        if (dragStateRef.current.moved) return;
                        if (!isExpanded) {
                            openExpanded();
                        } else {
                            setIsPlaying(p => !p);
                        }
                    }}
                    style={{
                        position: 'relative',
                        width: isExpanded ? 'min(100%, 540px)' : '100%',
                        height: isExpanded ? 'min(78vh, 720px)' : '100%',
                        borderRadius: isExpanded ? '18px' : '10px',
                        overflow: 'hidden',
                        background: isExpanded ? '#000000' : '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        poster={posterUrl}
                        muted={isMuted}
                        loop
                        playsInline
                        preload="metadata"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: isExpanded ? 'contain' : 'cover',
                            display: 'block',
                            pointerEvents: 'none'
                        }}
                    />

                    {/* Loading Spinner */}
                    {isBuffering && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0.22)',
                                pointerEvents: 'none'
                            }}
                        >
                            <div
                                style={{
                                    width: isExpanded ? '34px' : '20px',
                                    height: isExpanded ? '34px' : '20px',
                                    borderRadius: '50%',
                                    border: '2px solid rgba(255,255,255,0.35)',
                                    borderTopColor: '#ffffff',
                                    animation: 'spin 0.8s linear infinite'
                                }}
                            />
                        </div>
                    )}

                    {/* Top-Right Close (✕) Button — matches reference screenshot */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isExpanded) {
                                closeExpanded();
                            } else {
                                setIsClosed(true);
                            }
                        }}
                        aria-label={isExpanded ? 'Minimize video' : 'Close mini video'}
                        style={{
                            position: 'absolute',
                            top: isExpanded ? '14px' : '5px',
                            right: isExpanded ? '14px' : '5px',
                            width: isExpanded ? '36px' : '22px',
                            height: isExpanded ? '36px' : '22px',
                            borderRadius: '50%',
                            background: 'rgba(17, 17, 17, 0.72)',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            padding: 0,
                            zIndex: 5
                        }}
                    >
                        <X size={isExpanded ? 18 : 13} />
                    </button>

                    {/* Top-Left Minimize Button (in mini-mode) */}
                    {!isExpanded && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMinimized(true);
                            }}
                            aria-label="Minimize video to pill"
                            title={isRTL ? 'تصغير' : 'Minimize'}
                            style={{
                                position: 'absolute',
                                top: '5px',
                                left: '5px',
                                width: '22px',
                                height: '22px',
                                borderRadius: '50%',
                                background: 'rgba(17, 17, 17, 0.58)',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0,
                                zIndex: 5
                            }}
                        >
                            <Minimize2 size={11} />
                        </button>
                    )}

                    {/* Bottom Controls Bar */}
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'absolute',
                            bottom: isExpanded ? '14px' : '4px',
                            left: isExpanded ? '14px' : '5px',
                            right: isExpanded ? '14px' : '5px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            zIndex: 5
                        }}
                    >
                        {/* Play / Pause button */}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsPlaying(p => !p);
                            }}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            style={{
                                width: isExpanded ? '36px' : '22px',
                                height: isExpanded ? '36px' : '22px',
                                borderRadius: '50%',
                                background: 'rgba(17, 17, 17, 0.68)',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            {isPlaying ? <Pause size={isExpanded ? 16 : 11} /> : <Play size={isExpanded ? 16 : 11} />}
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: isExpanded ? '10px' : '4px' }}>
                            {/* Mute / Unmute button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMuted(m => !m);
                                }}
                                aria-label={isMuted ? 'Unmute' : 'Mute'}
                                style={{
                                    width: isExpanded ? '36px' : '22px',
                                    height: isExpanded ? '36px' : '22px',
                                    borderRadius: '50%',
                                    background: 'rgba(17, 17, 17, 0.68)',
                                    color: '#ffffff',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                {isMuted ? <VolumeX size={isExpanded ? 16 : 11} /> : <Volume2 size={isExpanded ? 16 : 11} />}
                            </button>

                            {/* Expand / Shrink button */}
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isExpanded) closeExpanded();
                                    else openExpanded();
                                }}
                                aria-label={isExpanded ? 'Shrink to mini player' : 'Expand video'}
                                style={{
                                    width: isExpanded ? '36px' : '22px',
                                    height: isExpanded ? '36px' : '22px',
                                    borderRadius: '50%',
                                    background: 'rgba(17, 17, 17, 0.68)',
                                    color: '#ffffff',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            >
                                {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={11} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Expanded Caption Footer */}
                {isExpanded && productName && (
                    <div
                        style={{
                            marginTop: '12px',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: 700,
                            textAlign: 'center',
                            maxWidth: '520px',
                            padding: '0 12px'
                        }}
                    >
                        {productName}
                    </div>
                )}
            </div>
        </>
    );
};

export default React.memo(FloatingMiniVideoPlayer);
